"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { Toaster } from "@/components/ui/toaster"
import { motion, AnimatePresence } from "framer-motion"
import { usePathname } from "next/navigation";
import Link from 'next/link';
import dynamic from "next/dynamic"
import { Menu, X } from "lucide-react"
import ErrorBoundary from "@/components/error-boundary"
import ThemeToggleButton from "@/components/theme-toggle-button"

// Floating widget with no SSR value — deferring it keeps its JS (and the chat
// UI it pulls in) out of the initial bundle, so it stops competing with the
// hero for main-thread time during the LCP window.
const AIChatbot = dynamic(() => import("@/components/ai-chatbot"), {
  ssr: false,
})

// Points at the real /projects page, not the homepage anchor — otherwise
// /projects has no internal inbound links at all.
const navLinks = [
  { href: "/", label: "Home" },
  { href: "/blog", label: "Writing" },
  { href: "/projects", label: "Projects" },
  { href: "/hire", label: "Hire Me" },
]

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const [menuOpen, setMenuOpen] = useState(false)

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href)

  // This layout stays mounted across route changes, so without this the menu
  // would still be sitting open on top of the page the user just navigated to.
  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!menuOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)

    // Without this the page keeps scrolling under the open menu when a thumb
    // drags across it, which on a 13,000px-tall page loses the user's place.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      window.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [menuOpen])

  return (
    <>
      {/* Sticky so nav stays reachable — the homepage alone is ~13,000px tall,
          which is a long way back to the top on a phone. */}
      <header className="sticky top-0 z-50 border-b bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/75">
        <div className="container mx-auto flex h-16 items-center justify-between gap-3">
          {/* min-w-0 lets the name truncate rather than shove the nav off-screen */}
          <Link
            href="/"
            className="flex min-w-0 items-center self-stretch truncate text-lg font-bold text-primary transition-opacity hover:opacity-80 sm:text-xl"
          >
            Sakthiish Vijayadass
          </Link>

          <nav className="hidden items-center gap-5 md:flex" aria-label="Main">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={
                  isActive(link.href) && link.href !== "/"
                    ? 'font-semibold text-accent border-b-2 border-accent pb-0.5'
                    : 'hover:text-primary transition-colors'
                }
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/blog#subscribe"
              className="inline-flex h-9 items-center rounded-md bg-primary px-3.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Subscribe
            </Link>
            <ThemeToggleButton />
          </nav>

          <div className="flex shrink-0 items-center gap-1 md:hidden">
            <ThemeToggleButton />
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-input transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Plain conditional render rather than an AnimatePresence exit: the
            panel then unmounts synchronously with the state, so it can never
            be left covering the page if an exit animation doesn't finish. The
            mount fade is cosmetic and needs no height measurement. */}
        {menuOpen && (
          <motion.div
            id="mobile-nav"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="border-t bg-background md:hidden"
          >
            <nav aria-label="Main" className="container py-3">
                <ul className="flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        aria-current={isActive(link.href) ? "page" : undefined}
                        className={`flex min-h-[48px] items-center rounded-md px-3 text-base transition-colors ${
                          isActive(link.href)
                            ? "bg-secondary font-semibold text-accent"
                            : "hover:bg-secondary"
                        }`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                  <li className="pt-2">
                    <Link
                      href="/blog#subscribe"
                      className="flex min-h-[48px] w-full items-center justify-center rounded-md bg-primary px-4 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      Subscribe
                    </Link>
                  </li>
              </ul>
            </nav>
          </motion.div>
        )}
      </header>

      {/* `initial={false}` skips the enter animation on the very first render.
          Without it the whole page ships from the server at opacity: 0 and
          nothing paints until the JS bundle has downloaded and hydrated,
          which pushed First Contentful Paint to ~3.2s despite a ~39ms TTFB.
          Route-to-route transitions still animate. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <ErrorBoundary>
            <main className="flex-grow">{children}</main>
          </ErrorBoundary>
        </motion.div>
      </AnimatePresence>
      <Toaster />
      <AIChatbot />
    </>
  )
}
